/**
 * RFIDetailPanel Component
 * Full detail view for a single RFI including file attachments,
 * answer submission, status controls, and email send.
 */
import { useState, useRef } from 'react';
import {
  RFI,
  RFIFile,
  RFI_STATUS_CONFIG,
  VALID_RFI_TRANSITIONS,
  ImpactLevel,
  IMPACT_LEVEL_LABELS,
} from '@/types/rfis';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  Trash2,
  Upload,
  Send,
  X,
  FilePlus,
} from 'lucide-react';
import {
  useRFI,
  useUpdateRFIStatus,
  useSubmitRFIAnswer,
  useSendRFIEmail,
  useUploadRFIFile,
  useDeleteRFIFile,
  useCreateSIFromRFI,
} from '@/hooks';
import { api } from '@/lib/api';

interface RFIDetailPanelProps {
  rfiId: string | null;
  onClose: () => void;
  onEdit: (rfi: RFI) => void;
}

function formatDate(val: string | null | undefined) {
  if (!val) return null;
  return new Date(val).toLocaleDateString();
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FilesSection({
  rfiId,
  files,
  fileType,
  label,
}: {
  rfiId: string;
  files: RFIFile[];
  fileType: 'question' | 'answer';
  label: string;
}) {
  const uploadRef = useRef<HTMLInputElement>(null);
  const uploadFile = useUploadRFIFile();
  const deleteFile = useDeleteRFIFile();

  const filtered = files.filter((f) => f.file_type === fileType);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile.mutateAsync({ rfiId, file, fileType });
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const handleDelete = async (fileId: string) => {
    if (confirm('Delete this file?')) {
      await deleteFile.mutateAsync({ rfiId, fileId });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {label} Files
        </h4>
        <div>
          <input
            ref={uploadRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => uploadRef.current?.click()}
            disabled={uploadFile.isPending}
          >
            <Upload className="h-3 w-3 mr-1" />
            {uploadFile.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No {label.toLowerCase()} files attached.</p>
      ) : (
        <div className="space-y-1">
          {filtered.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-2 rounded border bg-background text-sm"
            >
              <span className="truncate flex-1">{file.file_name}</span>
              <span className="text-muted-foreground mx-2 whitespace-nowrap">
                {formatFileSize(file.file_size)}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => api.rfis.files.download(rfiId, file.id, file.file_name)}
                  title="Download"
                >
                  <Download className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleDelete(file.id)}
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RFIDetailPanel({ rfiId, onClose, onEdit }: RFIDetailPanelProps) {
  const { data, isLoading } = useRFI(rfiId);
  const updateStatus = useUpdateRFIStatus();
  const submitAnswer = useSubmitRFIAnswer();
  const sendEmail = useSendRFIEmail();
  const createSIFromRFI = useCreateSIFromRFI();

  const [answerText, setAnswerText] = useState('');
  const [sendEmail_recipient, setSendEmailRecipient] = useState('');
  const [sendEmail_note, setSendEmailNote] = useState('');
  const [showSendForm, setShowSendForm] = useState(false);
  const [showAnswerForm, setShowAnswerForm] = useState(false);

  const rfi = data?.rfi;
  const files = data?.files ?? [];

  const handleClose = () => {
    setAnswerText('');
    setSendEmailRecipient('');
    setSendEmailNote('');
    setShowSendForm(false);
    setShowAnswerForm(false);
    onClose();
  };

  const handleStatusChange = async (newStatus: 'open' | 'answered' | 'closed') => {
    if (!rfi) return;
    await updateStatus.mutateAsync({ rfiId: rfi.id, status: newStatus });
  };

  const handleSubmitAnswer = async () => {
    if (!rfi || !answerText.trim()) return;
    await submitAnswer.mutateAsync({ rfiId: rfi.id, data: { answer: answerText.trim() } });
    setAnswerText('');
    setShowAnswerForm(false);
  };

  const handleSendEmail = async () => {
    if (!rfi || !sendEmail_recipient.trim()) return;
    await sendEmail.mutateAsync({
      rfiId: rfi.id,
      data: {
        recipient_email: sendEmail_recipient.trim(),
        custom_note: sendEmail_note || null,
      },
    });
    setSendEmailRecipient('');
    setSendEmailNote('');
    setShowSendForm(false);
  };

  const renderImpact = (val: ImpactLevel | null | undefined) => {
    if (!val || val === 'none') return null;
    return IMPACT_LEVEL_LABELS[val];
  };

  return (
    <Dialog open={!!rfiId} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading || !rfi ? (
          <>
            <DialogTitle className="sr-only">RFI</DialogTitle>
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-sm font-mono text-muted-foreground">{rfi.rfi_number}</span>
                  <DialogTitle className="mt-1">{rfi.subject}</DialogTitle>
                </div>
                <Badge className={RFI_STATUS_CONFIG[rfi.status].className}>
                  {RFI_STATUS_CONFIG[rfi.status].label}
                </Badge>
              </div>
            </DialogHeader>

            {/* Core fields */}
            <div className="space-y-3">
              {rfi.question && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Question</p>
                  <p className="text-sm whitespace-pre-wrap">{rfi.question}</p>
                </div>
              )}
              {rfi.background && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Background</p>
                  <p className="text-sm whitespace-pre-wrap">{rfi.background}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {rfi.drawing_reference && (
                  <div>
                    <span className="text-muted-foreground">Drawing Ref: </span>
                    {rfi.drawing_reference}
                  </div>
                )}
                {rfi.spec_reference && (
                  <div>
                    <span className="text-muted-foreground">Spec Ref: </span>
                    {rfi.spec_reference}
                  </div>
                )}
                {renderImpact(rfi.cost_impact) && (
                  <div>
                    <span className="text-muted-foreground">Cost Impact: </span>
                    {renderImpact(rfi.cost_impact)}
                  </div>
                )}
                {renderImpact(rfi.schedule_impact) && (
                  <div>
                    <span className="text-muted-foreground">Schedule Impact: </span>
                    {renderImpact(rfi.schedule_impact)}
                  </div>
                )}
                {rfi.assigned_to && (
                  <div>
                    <span className="text-muted-foreground">Assigned To: </span>
                    {rfi.assigned_to}
                    {rfi.assigned_to_email && ` (${rfi.assigned_to_email})`}
                  </div>
                )}
                {rfi.date_due && (
                  <div>
                    <span className="text-muted-foreground">Due: </span>
                    {formatDate(rfi.date_due)}
                  </div>
                )}
                {rfi.date_issued && (
                  <div>
                    <span className="text-muted-foreground">Issued: </span>
                    {formatDate(rfi.date_issued)}
                  </div>
                )}
                {rfi.date_answered && (
                  <div>
                    <span className="text-muted-foreground">Answered: </span>
                    {formatDate(rfi.date_answered)}
                  </div>
                )}
              </div>
            </div>

            {/* Answer */}
            {rfi.answer && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Answer</p>
                  <p className="text-sm whitespace-pre-wrap">{rfi.answer}</p>
                  {rfi.answered_by && (
                    <p className="text-xs text-muted-foreground mt-1">
                      — {rfi.answered_by}
                      {rfi.answered_by_email && ` (${rfi.answered_by_email})`}
                    </p>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Files */}
            <div className="space-y-4">
              <FilesSection rfiId={rfi.id} files={files} fileType="question" label="Question" />
              <FilesSection rfiId={rfi.id} files={files} fileType="answer" label="Answer" />
            </div>

            <Separator />

            {/* Workflow actions */}
            <div className="space-y-3">
              {/* Send email form */}
              {(rfi.status === 'draft' || rfi.status === 'open') && (
                <div>
                  {!showSendForm ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Prefill recipient from assigned_to_email so users don't
                        // have to retype data they already entered on the RFI.
                        if (!sendEmail_recipient && rfi.assigned_to_email) {
                          setSendEmailRecipient(rfi.assigned_to_email);
                        }
                        setShowSendForm(true);
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send RFI
                    </Button>
                  ) : (
                    <div className="border rounded p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Send RFI Email</p>
                        <Button variant="ghost" size="icon" onClick={() => setShowSendForm(false)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="send-recipient">Recipient Email *</Label>
                        <Input
                          id="send-recipient"
                          type="email"
                          placeholder="architect@firm.com"
                          value={sendEmail_recipient}
                          onChange={(e) => setSendEmailRecipient(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="send-note">Custom Note (optional)</Label>
                        <Textarea
                          id="send-note"
                          rows={2}
                          placeholder="Any additional context..."
                          value={sendEmail_note}
                          onChange={(e) => setSendEmailNote(e.target.value)}
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={handleSendEmail}
                        disabled={!sendEmail_recipient.trim() || sendEmail.isPending}
                      >
                        {sendEmail.isPending ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit answer form */}
              {rfi.status === 'open' && (
                <div>
                  {!showAnswerForm ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAnswerForm(true)}
                    >
                      Submit Answer
                    </Button>
                  ) : (
                    <div className="border rounded p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Submit Answer</p>
                        <Button variant="ghost" size="icon" onClick={() => setShowAnswerForm(false)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <Textarea
                        rows={4}
                        placeholder="Enter the answer to this RFI..."
                        value={answerText}
                        onChange={(e) => setAnswerText(e.target.value)}
                      />
                      <Button
                        size="sm"
                        onClick={handleSubmitAnswer}
                        disabled={!answerText.trim() || submitAnswer.isPending}
                      >
                        {submitAnswer.isPending ? 'Submitting...' : 'Submit Answer'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Status transitions */}
              {VALID_RFI_TRANSITIONS[rfi.status].length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {VALID_RFI_TRANSITIONS[rfi.status]
                    .filter((s) => !(s === 'open' || s === 'answered')) // exclude send-handled transitions
                    .map((nextStatus) => (
                      <Button
                        key={nextStatus}
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(nextStatus as 'open' | 'answered' | 'closed')}
                        disabled={updateStatus.isPending}
                      >
                        Mark as {RFI_STATUS_CONFIG[nextStatus].label}
                      </Button>
                    ))}
                </div>
              )}

              {/* Create SI from answered RFI */}
              {rfi.status === 'answered' && (
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => createSIFromRFI.mutateAsync({ projectId: rfi.project_id, rfiId: rfi.id })}
                    disabled={createSIFromRFI.isPending}
                    title="Create a Supplemental Instruction from this RFI's answer"
                  >
                    <FilePlus className="h-4 w-4 mr-2" />
                    {createSIFromRFI.isPending ? 'Creating SI...' : 'Create SI'}
                  </Button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(rfi)}>
                Edit RFI
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
